package screens.post

import androidx.compose.runtime.*
import com.example.readingnotes.di.AppModule
import com.example.readingnotes.presentation.postlist.PostListEvent
import com.example.readingnotes.presentation.postlist.PostListViewModel
import com.example.readingnotes.presentation.postlist.SortOption
import com.example.readingnotes.presentation.theme.ThemeState
import com.example.readingnotes.presentation.theme.ThemeViewModel
import com.example.readingnotes.util.DateFormatter
import design.AccessibilityGuidelines
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.internal.*
import styles.AppStyleSheet
import utils.SseClient
import utils.SseErrorType
import utils.WebPushManager
import utils.Notification
import utils.getItem
import utils.setItem
import org.w3c.notifications.NotificationPermission
import com.example.readingnotes.domain.usecase.GetNotificationsUseCase
import components.NotificationPermissionPrompt

/**
 * Highlight search query matches in text with HTML mark tags
 */
fun highlightText(text: String, query: String): String {
    if (query.isBlank() || text.isBlank()) return text
    val escapedQuery = Regex.escape(query)
    val regex = Regex("($escapedQuery)", RegexOption.IGNORE_CASE)
    return regex.replace(text) { matchResult ->
        "<mark style=\"background-color: #ffeb3b; padding: 0 2px; border-radius: 2px;\">${matchResult.value}</mark>"
    }
}

/**
 * Create HTML content with highlighted text
 */
fun createHighlightedHtml(content: String, query: String): String {
    val preview = content
        .take(200)
        .replace("\n", " ")
        .replace(Regex("\\[.*?\\]\\(.*?\\)"), "画像")
    return highlightText(if (preview.length > 200) "$preview..." else preview, query)
}

@Composable
fun PostListScreen(
    onPostClick: (String) -> Unit,
    onAdminClick: () -> Unit,
    isLoggedIn: Boolean = false,
    onLogoutClick: () -> Unit = {},
    appModule: AppModule,
    appScope: CoroutineScope,
    themeViewModel: ThemeViewModel,
    onNotificationHistoryClick: () -> Unit = {},
    onNotificationSettingsClick: () -> Unit = {},
) {
    val themeState by themeViewModel.state.collectAsState()
    val viewModel =
        remember {
            PostListViewModel(
                appModule.loadPostListUseCase,
                appModule.loadPostListWithPaginationUseCase,
                appModule.searchPostsUseCase,
                appModule.loadPopularTagsUseCase,
                appModule.getSearchSuggestionsUseCase,
                appModule.getSearchHistoryUseCase,
                appModule.clearSearchHistoryUseCase,
                appScope
            )
        }
    val state by viewModel.state.collectAsState()

    // SSE client for real-time updates
    val apiBaseUrl = js("window.API_BASE_URL || 'http://localhost:3001'") as String
    val sseClient = remember { SseClient.create(apiBaseUrl) }

    // Push notification state
    var pushNotificationEnabled by remember { mutableStateOf(false) }
    var pushNotificationSupported by remember { mutableStateOf(false) }
    val webPushManager = remember { WebPushManager(apiBaseUrl) }

    // Unread notification count state
    var unreadCount by remember { mutableStateOf(0) }

    // Notification permission prompt state
    var showPermissionPrompt by remember { mutableStateOf(false) }
    val permissionPromptShownKey = "notification_permission_prompt_shown"

    // Check if permission prompt was shown before
    val promptWasShown = remember {
        getItem(permissionPromptShownKey)?.toBoolean() ?: false
    }

    // Check push notification support and permission on mount
    LaunchedEffect(Unit) {
        pushNotificationSupported = webPushManager.isSupported()
        val permission = Notification.permission
        pushNotificationEnabled = permission == "granted"

        // Fetch unread notification count
        appModule.getNotificationsUseCase(limit = 100, offset = 0, unreadOnly = true)
            .onSuccess { paginatedNotifications ->
                unreadCount = paginatedNotifications.total
            }
            .onFailure { error ->
                console.error("Failed to fetch unread count:", error)
            }

        // Show permission prompt if:
        // 1. Push notifications are supported
        // 2. Prompt hasn't been shown before
        // 3. Permission is not yet determined (default)
        if (pushNotificationSupported && !promptWasShown && permission == "default") {
            kotlinx.coroutines.delay(1000) // Small delay for better UX
            showPermissionPrompt = true
        }
    }

    // Handle push notification toggle
    fun togglePushNotifications() {
        if (pushNotificationEnabled) {
            // Unsubscribe
            appScope.launch {
                webPushManager.unsubscribe(
                    onSuccess = {
                        pushNotificationEnabled = false
                        console.log("Unsubscribed from push notifications")
                    },
                    onError = { error ->
                        console.error("Failed to unsubscribe:", error)
                    }
                )
            }
        } else {
            // Subscribe
            appScope.launch {
                webPushManager.subscribe(
                    onSuccess = {
                        pushNotificationEnabled = true
                        console.log("Subscribed to push notifications")
                    },
                    onError = { error ->
                        console.error("Failed to subscribe:", error)
                    }
                )
            }
        }
    }

    LaunchedEffect(Unit) {
        viewModel.onEvent(PostListEvent.OnAppear)

        // Set up SSE error handler for graceful degradation
        sseClient.onError { error ->
            when (error.type) {
                SseErrorType.FATAL -> {
                    // Fatal errors (404, 401) - log and continue without notifications
                    console.warn("SSE notifications unavailable:", error.message)
                }
                SseErrorType.TRANSIENT -> {
                    // Temporary network errors - log but don't spam
                    console.log("SSE temporary error:", error.message)
                }
                else -> {
                    console.log("SSE unknown error:", error.message)
                }
            }
        }

        // Set up SSE event listeners
        sseClient.addEventListener("post.created") { event ->
            console.log("SSE: Post created event received", event.data)
            // Announce to screen readers
            AccessibilityGuidelines.announce("新しい投稿が追加されました")
            // Reload posts to get the latest list
            viewModel.onEvent(PostListEvent.OnAppear)
        }

        sseClient.addEventListener("post.deleted") { event ->
            console.log("SSE: Post deleted event received", event.data)
            // Reload posts to get the latest list
            viewModel.onEvent(PostListEvent.OnAppear)
        }

        sseClient.addEventListener("comment.created") { event ->
            console.log("SSE: Comment created event received", event.data)
            // Announce to screen readers
            AccessibilityGuidelines.announce("新しいコメントが追加されました")
        }

        // Connect to SSE with error handling
        // Note: SSE notifications are not fully implemented on backend yet
        // This will gracefully fail if the endpoint is not available
        try {
            sseClient.connect()
        } catch (e: Exception) {
            console.warn("SSE connection failed - notifications unavailable:", e.message)
        }
    }

    // Cleanup SSE connection on dispose
    DisposableEffect(Unit) {
        onDispose {
            sseClient.disconnect()
        }
    }

    // Accessibility skip link - allows keyboard users to jump to main content
    A(href = "#main-content", attrs = {
        classes(AppStyleSheet.a11ySkipLink)
        attr("data-testid", "skip-link")
    }) {
        Text("メインコンテンツへスキップ")
    }

    // Infinite scroll implementation - temporary disabled for compatibility
    /*
    DisposableEffect(Unit) {
        val handleScroll: (Event) -> Unit = {
            if (window.innerHeight + window.scrollY >= document.body.scrollHeight - 100) {
                // Near bottom of page
                if (state.hasMore && !state.isLoadingMore && !state.isLoading) {
                    viewModel.onEvent(PostListEvent.OnLoadMore)
                }
            }
        }

        window.addEventListener("scroll", handleScroll)
        onDispose {
            window.removeEventListener("scroll", handleScroll)
        }
    }
    */

    Div({
        classes(AppStyleSheet.header)
        attr("data-testid", "mobile-nav")
    }) {
        H1 { Text("読書メモ") }
        Div({ classes(AppStyleSheet.headerButtons) }) {
            // Notification history button with badge
            Button({
                classes(AppStyleSheet.iconButton)
                onClick { onNotificationHistoryClick() }
                attr("aria-label", "通知履歴を開く")
                attr("title", "通知履歴")
                style {
                    position(Position.Relative)
                }
            }) {
                Text("📋")
                if (unreadCount > 0) {
                    Span({
                        classes(AppStyleSheet.notificationBadge)
                    }) {
                        Text(if (unreadCount > 99) "99+" else unreadCount.toString())
                    }
                }
            }

            // Notification settings button
            Button({
                classes(AppStyleSheet.iconButton)
                onClick { onNotificationSettingsClick() }
                attr("aria-label", "通知設定を開く")
                attr("title", "通知設定")
            }) {
                Text("⚙️")
            }

            // Push notification toggle button
            if (pushNotificationSupported) {
                Button({
                    classes(AppStyleSheet.iconButton)
                    onClick { togglePushNotifications() }
                    attr("aria-label", if (pushNotificationEnabled) "プッシュ通知をオフにする" else "プッシュ通知をオンにする")
                    attr("title", if (pushNotificationEnabled) "プッシュ通知: オン" else "プッシュ通知: オフ")
                    style {
                        if (pushNotificationEnabled) {
                            backgroundColor(Color("#4CAF50"))
                        }
                    }
                }) {
                    Text(if (pushNotificationEnabled) "🔔" else "🔕")
                }
            }

            // Theme toggle button
            Button({
                classes(AppStyleSheet.iconButton)
                onClick {
                    // Update KMP common ThemeViewModel state
                    themeViewModel.toggleTheme()
                    // Also sync with Web's design.Theme (CSS variables)
                    design.Theme.toggleTheme()
                }
                attr("aria-label", if (themeState.isDarkMode) "ライトモードに切り替え" else "ダークモードに切り替え")
                attr("title", if (themeState.isDarkMode) "ライトモード" else "ダークモード")
                attr("data-testid", "theme-toggle")
            }) {
                Text(if (themeState.isDarkMode) "☀️" else "🌙")
            }
            if (isLoggedIn) {
                // Single "管理" button for logged-in users (consolidates admin functions)
                Button({
                    classes(AppStyleSheet.primaryButton)
                    onClick { onAdminClick() }
                }) { Text("管理") }
            } else {
                // Admin login button for non-logged-in users
                Button({
                    classes(AppStyleSheet.secondaryButton)
                    onClick { onAdminClick() }
                }) { Text("ログイン") }
            }
        }
    }

    // Search and filter section
    Div({ classes(AppStyleSheet.searchSection) }) {
        // Search bar with suggestions
        Div({
            classes(AppStyleSheet.searchBar)
            style {
                property("position", "relative")
            }
        }) {
            Input(type = InputType.Text) {
                classes(AppStyleSheet.searchInput)
                attr("placeholder", "検索...")
                attr("aria-label", "投稿を検索")
                attr("aria-autocomplete", "list")
                // Only add aria-controls when the suggestions list is visible and rendered
                // Note: aria-expanded is not allowed on input elements, so we use aria-haspopup instead
                if (state.showSuggestions && state.searchSuggestions.isNotEmpty()) {
                    attr("aria-controls", "search-suggestions-list")
                }
                attr("aria-haspopup", "listbox")
                attr("data-testid", "search-input")
                value(state.searchQuery)
                onInput {
                    viewModel.onEvent(PostListEvent.OnSearchQueryChanged(it.value))
                }
                onFocus { viewModel.onEvent(PostListEvent.OnShowSuggestions) }
            }
            if (state.searchQuery.isNotBlank()) {
                Button({
                    classes(AppStyleSheet.clearSearchButton)
                    onClick { viewModel.onEvent(PostListEvent.OnClearSearch) }
                    attr("aria-label", "検索をクリア")
                }) { Text("クリア") }
            }

            // Search suggestions dropdown
            if (state.showSuggestions && state.searchSuggestions.isNotEmpty()) {
                Div({
                    attr("id", "search-suggestions-list")
                    attr("role", "listbox")
                    style {
                        property("position", "absolute")
                        property("top", "100%")
                        property("left", "0")
                        property("right", "0")
                        property("background", "white")
                        property("border", "1px solid #ddd")
                        property("border-radius", "4px")
                        property("box-shadow", "0 4px 6px rgba(0,0,0,0.1)")
                        property("z-index", "1000")
                        property("max-height", "200px")
                        property("overflow-y", "auto")
                        property("margin-top", "4px")
                    }
                }) {
                    // Show suggestions first
                    if (state.searchSuggestions.isNotEmpty()) {
                        Div({
                            style {
                                property("padding", "4px 8px")
                                property("font-size", "12px")
                                property("color", "#666")
                                property("background", "#f8f9fa")
                                property("border-bottom", "1px solid #eee")
                            }
                        }) {
                            Text("サジェスト")
                        }
                        state.searchSuggestions.forEach { suggestion ->
                            Div({
                                attr("role", "option")
                                style {
                                    property("padding", "8px 12px")
                                    property("cursor", "pointer")
                                    property("border-bottom", "1px solid #f0f0f0")
                                }
                                onClick {
                                    viewModel.onEvent(PostListEvent.OnSearchQueryChanged(suggestion))
                                    viewModel.onEvent(PostListEvent.OnHideSuggestions)
                                }
                            }) {
                                Text(suggestion)
                            }
                        }
                    }

                    // Show search history below suggestions
                    if (state.searchHistory.isNotEmpty()) {
                        if (state.searchSuggestions.isNotEmpty()) {
                            Div({
                                style {
                                    property("padding", "4px 8px")
                                    property("font-size", "12px")
                                    property("color", "#666")
                                    property("background", "#f8f9fa")
                                    property("border-top", "1px solid #ddd")
                                    property("border-bottom", "1px solid #eee")
                                    property("margin-top", "4px")
                                }
                            }) {
                                Text("最近の検索")
                            }
                        } else {
                            Div({
                                style {
                                    property("padding", "4px 8px")
                                    property("font-size", "12px")
                                    property("color", "#666")
                                    property("background", "#f8f9fa")
                                    property("border-bottom", "1px solid #eee")
                                }
                            }) {
                                Text("最近の検索")
                            }
                        }
                        state.searchHistory.take(5).forEach { historyItem ->
                            Div({
                                attr("role", "option")
                                style {
                                    property("padding", "8px 12px")
                                    property("cursor", "pointer")
                                    property("border-bottom", "1px solid #f0f0f0")
                                    property("display", "flex")
                                    property("justify-content", "space-between")
                                    property("align-items", "center")
                                }
                                onClick {
                                    viewModel.onEvent(PostListEvent.OnSearchQueryChanged(historyItem))
                                    viewModel.onEvent(PostListEvent.OnHideSuggestions)
                                }
                            }) {
                                Text(historyItem)
                                Span({
                                    style {
                                        property("font-size", "11px")
                                        property("color", "#999")
                                    }
                                }) {
                                    Text("履歴")
                                }
                            }
                        }
                        // Clear history button
                        Div({
                            style {
                                property("padding", "8px 12px")
                                property("text-align", "center")
                            }
                            onClick {
                                viewModel.onEvent(PostListEvent.OnClearSearchHistory)
                            }
                        }) {
                            Span({
                                style {
                                    property("font-size", "12px")
                                    property("color", "#dc3545")
                                    property("cursor", "pointer")
                                }
                            }) {
                                Text("履歴をクリア")
                            }
                        }
                    }
                }
            }
        }

        // Sort and filter controls
        Div({ classes(AppStyleSheet.controlsBar) }) {
            // Sort dropdown
            Div({ classes(AppStyleSheet.controlGroup) }) {
                Span({ classes("control-label") }) { Text("並び替え:") }
                Select({
                    classes(AppStyleSheet.selectInput)
                    attr("aria-label", "投稿の並び順を選択")
                    attr("value", when (state.sortBy) {
                        SortOption.NEWEST -> "newest"
                        SortOption.OLDEST -> "oldest"
                        SortOption.TITLE_ASC -> "title_asc"
                        SortOption.TITLE_DESC -> "title_desc"
                        else -> "newest"
                    })
                    onChange { event ->
                        val sortBy = when (event.target.asDynamic().value) {
                            "oldest" -> SortOption.OLDEST
                            "title_asc" -> SortOption.TITLE_ASC
                            "title_desc" -> SortOption.TITLE_DESC
                            else -> SortOption.NEWEST
                        }
                        viewModel.onEvent(PostListEvent.OnSortChanged(sortBy))
                    }
                }) {
                    Option("newest") { Text("新しい順") }
                    Option("oldest") { Text("古い順") }
                    Option("title_asc") { Text("タイトル (A-Z)") }
                    Option("title_desc") { Text("タイトル (Z-A)") }
                }
            }

            // Status filter dropdown
            Div({ classes(AppStyleSheet.controlGroup) }) {
                Span({ classes("control-label") }) { Text("ステータス:") }
                Select({
                    classes(AppStyleSheet.selectInput)
                    attr("aria-label", "投稿のステータスでフィルタ")
                    attr("value", state.statusFilter ?: "")
                    onChange { event ->
                        val value = event.target.asDynamic().value as? String
                        val statusFilter = if (value == "" || value == "all") null else value
                        viewModel.onEvent(PostListEvent.OnStatusFilterChanged(statusFilter))
                    }
                }) {
                    Option("all") { Text("すべて") }
                    Option("published") { Text("公開済み") }
                    Option("draft") { Text("下書き") }
                    Option("archived") { Text("アーカイブ") }
                }
            }

            // Tag filters (show popular tags as clickable chips)
            if (state.popularTags.isNotEmpty()) {
                Div({ classes(AppStyleSheet.controlGroup) }) {
                    Span({ classes("control-label") }) { Text("タグ:") }
                    state.popularTags.take(5).forEach { tagWithCount ->
                        val isSelected = state.selectedTags.contains(tagWithCount.tag.name)
                        Button({
                            attr("key", tagWithCount.tag.name)
                            classes(if (isSelected) "tag-chip selected" else "tag-chip")
                            attr("aria-pressed", isSelected.toString())
                            onClick {
                                viewModel.onEvent(PostListEvent.OnTagFilterToggled(tagWithCount.tag.name))
                            }
                            style {
                                property("margin", "2px 4px 2px 0")
                                property("padding", "4px 10px")
                                property("border-radius", "12px")
                                property("font-size", "12px")
                                property("cursor", "pointer")
                                property("background", if (isSelected) "#007bff" else "#f0f0f0")
                                property("color", if (isSelected) "white" else "#333")
                                property("border", "none")
                            }
                        }) {
                            Text("${tagWithCount.tag.name} (${tagWithCount.postCount})")
                        }
                    }
                    if (state.selectedTags.isNotEmpty()) {
                        Button({
                            classes("clear-tags-button")
                            onClick { viewModel.onEvent(PostListEvent.OnClearTagFilters) }
                            attr("aria-label", "タグフィルタをクリア")
                            style {
                                property("margin-left", "8px")
                                property("padding", "4px 8px")
                                property("font-size", "12px")
                                property("cursor", "pointer")
                            }
                        }) {
                            Text("クリア")
                        }
                    }
                }
            }
        }
    }

    when {
        state.isLoading && state.posts.isEmpty() && !state.isSearchActive -> Div({
            classes(AppStyleSheet.loading)
            attr("role", "status")
            attr("aria-live", "polite")
            attr("aria-label", "投稿一覧を読み込み中")
            style {
                property("display", "flex")
                property("flex-direction", "column")
                property("align-items", "center")
                property("gap", "16px")
            }
        }) {
            Div({ classes(AppStyleSheet.loadingSpinner) }) {}
            Text("読み込み中...")
        }
        state.errorMessage != null && state.posts.isEmpty() && !state.isSearchActive -> {
            Div({ classes(AppStyleSheet.error) }) {
                Text(state.errorMessage!!)
                Button({
                    classes(AppStyleSheet.primaryButton)
                    style {
                        marginTop(16.px)
                    }
                    onClick { viewModel.onEvent(PostListEvent.OnRetry) }
                }) { Text("リトライ") }
            }
        }
        else -> {
            // Determine which posts to display
            val displayPosts = if (state.isSearchActive) {
                state.searchResults
            } else {
                state.posts
            }

            // Show search result count if searching
            if (state.isSearchActive) {
                Div({
                    style {
                        padding(8.px, 0.px)
                        color(Color("#666"))
                        fontSize(14.px)
                    }
                }) {
                    Text("${displayPosts.size}件の検索結果 (${state.searchQuery})")
                }

                // Facet filters panel for search results
                Div({
                    style {
                        padding(16.px)
                        marginTop(16.px)
                        property("background", "#f8f9fa")
                        property("border-radius", "8px")
                        property("border", "1px solid #dee2e6")
                    }
                }) {
                    H3({
                        style {
                            property("margin", "0 0 12px 0")
                            property("font-size", "16px")
                            property("font-weight", "600")
                        }
                    }) {
                        Text("絞り込み")
                    }

                    // Tag facets - extract tags from search results
                    val resultTags = displayPosts
                        .mapNotNull { post -> post.tags }
                        .flatten()
                        .groupBy { it.name }
                        .map { (name, tags) -> name to tags.size }
                        .sortedByDescending { (_, count) -> count }
                        .take(10)

                    if (resultTags.isNotEmpty()) {
                        Div({
                            style {
                                property("margin-bottom", "16.px")
                            }
                        }) {
                            Span({
                                style {
                                    property("font-weight", "600")
                                    property("display", "block")
                                    property("margin-bottom", "8.px")
                                }
                            }) {
                                Text("タグ")
                            }
                            resultTags.forEach { (tagName, count) ->
                                val isSelected = state.selectedTags.contains(tagName)
                                Button({
                                    onClick {
                                        viewModel.onEvent(PostListEvent.OnTagFilterToggled(tagName))
                                    }
                                    attr("aria-pressed", isSelected.toString())
                                    style {
                                        property("margin", "4px 4px 4px 0")
                                        property("padding", "6px 12px")
                                        property("border-radius", "16px")
                                        property("font-size", "13.px")
                                        property("cursor", "pointer")
                                        property("background", if (isSelected) "#007bff" else "#ffffff")
                                        property("color", if (isSelected) "#ffffff" else "#333333")
                                        property("border", if (isSelected) "none" else "1px solid #dee2e6")
                                    }
                                }) {
                                    Text("$tagName ($count)")
                                }
                            }
                        }
                    }

                    // Date facets - group by month
                    val datesByMonth = displayPosts
                        .map { it.createdAt }
                        .mapNotNull { createdAt ->
                            // Format Instant as "YYYY年MM月"
                            try {
                                val formatted = DateFormatter.formatDateMonth(createdAt)
                                formatted
                            } catch (e: Exception) {
                                null
                            }
                        }
                        .groupBy { it }
                        .map { (month, dates) -> month to dates.size }
                        .sortedByDescending { it.first }
                        .take(6)

                    if (datesByMonth.isNotEmpty()) {
                        Div({
                            style {
                                property("margin-bottom", "16.px")
                            }
                        }) {
                            Span({
                                style {
                                    property("font-weight", "600")
                                    property("display", "block")
                                    property("margin-bottom", "8.px")
                                }
                            }) {
                                Text("日付")
                            }
                            datesByMonth.forEach { (month, count) ->
                                Button({
                                    style {
                                        property("margin", "4px 4px 4px 0")
                                        property("padding", "6px 12px")
                                        property("border-radius", "16px")
                                        property("font-size", "13.px")
                                        property("cursor", "pointer")
                                        property("background", "#ffffff")
                                        property("color", "#333333")
                                        property("border", "1px solid #dee2e6")
                                    }
                                }) {
                                    Text("$month ($count)")
                                }
                            }
                        }
                    }

                    // Clear all filters button
                    if (state.selectedTags.isNotEmpty()) {
                        Button({
                            onClick { viewModel.onEvent(PostListEvent.OnClearTagFilters) }
                            style {
                                property("padding", "8.px 16.px")
                                property("border-radius", "4.px")
                                property("font-size", "13.px")
                                property("cursor", "pointer")
                                property("background", "#6c757d")
                                property("color", "#ffffff")
                                property("border", "none")
                            }
                        }) {
                            Text("フィルタをクリア")
                        }
                    }
                }
            }

            Div({
                classes(AppStyleSheet.postList)
                attr("id", "main-content")
                attr("data-testid", "post-list")
            }) {
                if (displayPosts.isEmpty()) {
                    if (state.isSearchActive) {
                        Div({
                            style {
                                padding(40.px, 20.px)
                                textAlign("center")
                                color(Color("#6B7280"))
                            }
                            attr("data-testid", "no-results")
                        }) {
                            Text("検索結果が見つかりませんでした")
                        }
                    } else {
                        Div({
                            style {
                                padding(40.px, 20.px)
                                textAlign("center")
                                color(Color("#6B7280"))
                            }
                        }) {
                            Text("投稿がまだありません")
                        }
                    }
                } else {
                    displayPosts.forEach { post ->
                        Div({
                            classes(AppStyleSheet.postCard)
                            attr("role", "button")
                            attr("tabindex", "0")
                            attr("data-testid", "post-item")
                            onClick { onPostClick(post.id) }
                        }) {
                            // Highlight title if searching
                            if (state.isSearchActive && state.searchQuery.isNotBlank()) {
                                H2({
                                    attr("dangerouslySetInnerHTML", highlightText(post.title, state.searchQuery))
                                })
                            } else {
                                H2 { Text(post.title) }
                            }

                            // Content preview with optional highlighting
                            if (state.isSearchActive && state.searchQuery.isNotBlank()) {
                                Div({
                                    classes(AppStyleSheet.postContent)
                                    attr("dangerouslySetInnerHTML", createHighlightedHtml(post.contentMarkdown, state.searchQuery))
                                })
                            } else {
                                Div({
                                    classes(AppStyleSheet.postContent)
                                }) {
                                    val preview = post.contentMarkdown
                                        .take(100)
                                        .replace("\n", " ")
                                        .replace(Regex("\\[.*?\\]\\(.*?\\)"), "画像")
                                    Text(if (preview.length > 100) "$preview..." else preview)
                                }
                            }
                        }
                    }
                }

                // Load more button or loading indicator (only for non-search)
                if (!state.isSearchActive && state.hasMore) {
                    if (state.isLoadingMore) {
                        Div({
                            classes(AppStyleSheet.loading)
                            attr("role", "status")
                            attr("aria-live", "polite")
                            attr("aria-label", "さらに投稿を読み込み中")
                            style {
                                property("display", "flex")
                                property("flex-direction", "column")
                                property("align-items", "center")
                                property("gap", "16px")
                            }
                        }) {
                            Div({ classes(AppStyleSheet.loadingSpinner) }) {}
                            Text("読み込み中...")
                        }
                    } else {
                        Div({ style {
                            padding(20.px, 0.px)
                            textAlign("center")
                        } }) {
                            Button({
                                classes(AppStyleSheet.secondaryButton)
                                onClick { viewModel.onEvent(PostListEvent.OnLoadMore) }
                            }) {
                                Text("もっと読み込む")
                            }
                        }
                    }
                } else if (!state.isSearchActive && state.posts.isNotEmpty()) {
                    Div({
                        style {
                            padding(20.px, 0.px)
                            textAlign("center")
                            color(Color("#666"))
                        }
                    }) {
                        Text("すべての投稿を読み込みました")
                    }
                }
            }
        }
    }

    // Notification permission prompt
    NotificationPermissionPrompt(
        isVisible = showPermissionPrompt,
        onEnable = {
            // User clicked "Enable notifications"
            showPermissionPrompt = false
            setItem(permissionPromptShownKey, "true")
            // Subscribe to push notifications
            appScope.launch {
                webPushManager.subscribe(
                    onSuccess = {
                        pushNotificationEnabled = true
                        console.log("Subscribed to push notifications")
                    },
                    onError = { error ->
                        console.error("Failed to subscribe:", error)
                    }
                )
            }
        },
        onLater = {
            // User clicked "Later"
            showPermissionPrompt = false
            setItem(permissionPromptShownKey, "true")
        }
    )
}
